
import React, { useState } from "react";
import { useStudents } from "@/contexts";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Edit, Trash, ArrowLeft, Plus, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/formatters";
import { printNewStudentsListPaginatedAsJPG, printNewStudentsListPaginatedAsPDF } from "@/utils/printing/newStudentsListPaginatedPrinter";
import NewStudentsPageSelectionDialog from "@/components/students/NewStudentsPageSelectionDialog";
import NewStudentsFormatDialog from "@/components/students/NewStudentsFormatDialog";
import { toast } from "sonner";

const NewStudentsPage = () => {
  const { students, loading, deleteStudent } = useStudents();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPageSelection, setShowPageSelection] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);

  const getNewStudents = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return students
      .filter(student => new Date(student.createdAt) >= thirtyDaysAgo)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const newStudents = getNewStudents();

  const handleDownloadClick = () => {
    if (!newStudents || newStudents.length === 0) {
      toast.error('No new students to download');
      return;
    }

    // Always show the page selection dialog which now includes format selection
    setShowPageSelection(true);
  };

  const handleDirectFormatDownload = async (format: 'jpg' | 'pdf') => {
    const totalPages = Math.ceil(newStudents.length / 15);
    await handleDownloadNewStudentsList(format, 1, totalPages);
  };

  const handleDownloadNewStudentsList = async (format: 'jpg' | 'pdf', startPage?: number, endPage?: number) => {
    if (newStudents && newStudents.length > 0) {
      try {
        setIsDownloading(true);
        
        const newStudentsList = newStudents.map(student => ({
          id: student.id.toString(),
          name: student.name,
          fathersName: student.fathersName || 'N/A',
          program: student.programs[0]?.title || 'No Program',
          enrollmentDate: typeof student.createdAt === 'string' ? student.createdAt : student.createdAt.toISOString()
        }));
        
        if (startPage && endPage) {
          if (format === 'pdf') {
            await printNewStudentsListPaginatedAsPDF(newStudentsList, startPage, endPage);
          } else {
            await printNewStudentsListPaginatedAsJPG(newStudentsList, startPage, endPage);
          }
        } else {
          if (format === 'pdf') {
            await printNewStudentsListPaginatedAsPDF(newStudentsList, 1, Math.ceil(newStudentsList.length / 15));
          } else {
            await printNewStudentsListPaginatedAsJPG(newStudentsList, 1, Math.ceil(newStudentsList.length / 15));
          }
        }
      } catch (error) {
        console.error('Error downloading new students list:', error);
        toast.error('Failed to download new students list');
      } finally {
        setIsDownloading(false);
      }
    } else {
      toast.error('No new students to download');
    }
  };

  return (
    <>
      <div className="space-y-6 p-6">
        <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold">New Students</h2>
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <Link to="/students/new">
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add New Student
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadClick}
              disabled={isDownloading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isDownloading ? 'Downloading...' : 'Download List'}
            </Button>
          </div>
        </div>

        <div className="rounded-md border bg-card">
          <div className="p-4">
            <Input
              placeholder="Search new students by name or program..."
              className="max-w-full"
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Father's Name</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Enrollment Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : newStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    No new students in the last 30 days
                  </TableCell>
                </TableRow>
              ) : (
                newStudents.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.id}</TableCell>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>{student.fathersName || "N/A"}</TableCell>
                    <TableCell>
                      {student.programs[0]?.title || "No Program"}
                    </TableCell>
                    <TableCell>{formatDate(student.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/students/${student.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/students/edit/${student.id}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteStudent(student.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Page Selection Dialog with Format Selection */}
      <NewStudentsPageSelectionDialog
        isOpen={showPageSelection}
        onClose={() => setShowPageSelection(false)}
        totalNewStudents={newStudents?.length || 0}
        onDownload={handleDownloadNewStudentsList}
      />

      {/* Standalone Format Selection Dialog (kept for compatibility) */}
      <NewStudentsFormatDialog
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
        onDownload={handleDirectFormatDownload}
        totalStudents={newStudents?.length || 0}
      />
    </>
  );
};

export default NewStudentsPage;
